import React from 'react';
import axios from 'axios';
import qs from 'qs';
import moment from 'moment';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import BottomScrollListener from 'react-bottom-scroll-listener';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { withTranslation } from 'react-i18next';
import { withSnackbar } from 'notistack';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { setData } from '../../actions/index';
import ConfirmDialog from '../parts/ConfirmDialog';
import CalendarHeader from '../parts/CalendarHeader';
import ActivityEntry from '../parts/ActivityEntry';

const styles = theme => ({
  headerSection: {
    marginBottom: 20,
  },
  headerHeading: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  headerSubheading: {
    fontSize: 14,
  },
  calendarButton: {
    backgroundColor: '#f56a12',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#C24C03',
    },
  },
  activitySection: {
    marginTop: 40,
  },
  activitySectionHeading: {
    marginBottom: 20,
  },
  activitySectionSubHeading: {
    fontSize: 14,
    textAlign: 'center',
    color: '#0a72c7',
    margin: '30px 0 10px',
  },
  activityProgress: {
    textAlign: 'center',
    marginTop: 20,
  },
});

const mapStateToProps = state => {
  return {
    selectedPupil: state.selectedPupil,
    defaultDateRange: state.defaultDateRange,
  };
};

function mapDispatchToProps(dispatch) {
  return {
    setData: (data, type) => dispatch(setData(data, type)),
  };
}

class BehaviourPage extends React.Component {
  constructor(props) {
    super(props);

    this.activityDataBatchCount = 10;

    this.state = {
      dateStart: null,
      dateEnd: null,

      displayActivity: true,
      activityMeta: {},
      activityData: [],
      activityLoading: false,
      activityLoaded: false,
      hasMoreActivityData: true,

      behaviourMeta: {},
      behaviourData: [],
      behaviourLoading: false,
      behaviourLoaded: false,

      behaviourChartConfig: {
        chart: { height: 400 },
        title: { text: 'Loading ...' },
        credits: { enabled: false },
      },

      timeChartEnabled: false,
      timeChartConfig: {
        chart: { height: 400 },
        title: { text: 'Loading ...' },
        credits: { enabled: false },
      },

      isActivityEntryDeleteDialogOpen: false,
      selectedActivityEntry: null,
    };

    this.axiosCancelSource = axios.CancelToken.source();

    this.onActivityEntryDelete = this.onActivityEntryDelete.bind(this);
    this.onActvityEntryDeleteConfirmClick = this.onActvityEntryDeleteConfirmClick.bind(this);
    this.onRangeChanged = this.onRangeChanged.bind(this);
    this.onBottom = this.onBottom.bind(this);
    this.reloadData = this.reloadData.bind(this);
  }

  componentDidMount() {
    const {
      defaultDateRange,
    } = this.props;

    if (defaultDateRange !== null) {
      this.setState({
        dateStart: defaultDateRange[0],
        dateEnd: defaultDateRange[1],
      }, () => {
        this.reloadData();
      });
    } else {
      this.reloadData();
    }
  }

  componentWillUnmount() {
    this.axiosCancelSource.cancel();
  }

  onActivityEntryDelete(activityEntry) {
    this.setState({
      selectedActivityEntry: activityEntry,
      isActivityEntryDeleteDialogOpen: true,
    });
  }

  onActvityEntryDeleteConfirmClick() {
    const {
      enqueueSnackbar,
      t,
      selectedPupil,
    } = this.props;
    const {
      selectedActivityEntry,
    } = this.state;

    if (!selectedPupil || !selectedActivityEntry) {
      enqueueSnackbar(t('Unable to delete the activity entry.'), {
        variant: 'error',
        autoHideDuration: 2000,
      });

      return;
    }

    axios
      .post(
        `${window.apiUrl}/deleteparentbehaviour/${selectedPupil.id}`,
        qs.stringify({
          behaviour_id: selectedActivityEntry.id,
        }),
        {
          cancelToken: this.axiosCancelSource.token,
        }
      )
      .then((response) => {
        if (response.data.success) {
          this.setState({
            selectedActivityEntry: null,
          });

          enqueueSnackbar(t('You have successfully deleted the behaviour.'), {
            variant: 'success',
            autoHideDuration: 2000,
          });

          this.reloadData();
        } else {
          enqueueSnackbar(response.data.error, {
            variant: 'error',
            autoHideDuration: 2000,
          });
        }

        this.setState({
          isActivityEntryDeleteDialogOpen: false,
        })
      })
      .catch((thrown) => {});
  }

  onRangeChanged(range) {
    const dateStart = range[0].format('YYYY-MM-DD');
    const dateEnd = range[1].format('YYYY-MM-DD');

    this.setState({
      dateStart,
      dateEnd,
    }, () => {
      this.reloadData();
    });

    this.props.setData(range, 'defaultDateRange');
  }

  onBottom() {
    const {
      selectedPupil,
    } = this.props;
    const {
      dateStart,
      dateEnd,
      activityData,
      activityLoading,
      hasMoreActivityData,
    } = this.state;

    if (
      !selectedPupil ||
      !hasMoreActivityData ||
      activityLoading ||
      activityData.length === 0
    ) {
      return;
    }

    this.setState({
      activityLoading: true,
    });

    const lastActivity = activityData[activityData.length - 1];

    let dateQuery = '';
    if (dateStart && dateEnd) {
      dateQuery = `?from=${moment(dateStart).format('YYYY-MM-DD')}&to=${moment(dateEnd).format('YYYY-MM-DD')}&last_id=${lastActivity.id}`;
    } else {
      dateQuery = '?last_id=' + lastActivity.id;
    }

    dateQuery += '&limit=' + this.activityDataBatchCount;

    axios
      .get(
        `${window.apiUrl}/activity/${selectedPupil.id}${dateQuery}`,
        {
          cancelToken: this.axiosCancelSource.token,
        }
      )
      .then((response) => {
        if (response.data.success) {
          const newActivityData = activityData.concat(response.data.data);
          this.setState({
            activityData: newActivityData,
            activityLoading: false,
            hasMoreActivityData: response.data.data.length === this.activityDataBatchCount,
          });
        }
      })
      .catch((thrown) => {});
  }

  reloadData() {
    const {
      selectedPupil,
    } = this.props;
    const {
      dateStart,
      dateEnd,
    } = this.state;

    if (!selectedPupil) {
      return;
    }

    this.setState({
      activityLoading: true,
      behaviourLoading: true,
    });

    let dateQuery = '';
    if (dateStart && dateEnd) {
      dateQuery = `?from=${moment(dateStart).format('YYYY-MM-DD')}&to=${moment(dateEnd).format('YYYY-MM-DD')}`;
    }

    axios
      .get(
        `${window.apiUrl}/activity/${selectedPupil.id}${dateQuery}`,
        {
          cancelToken: this.axiosCancelSource.token,
        }
      )
      .then((response) => {
        if (response.data.success) {
          this.setState({
            activityMeta: response.data.meta,
            activityData: response.data.data,
            hasMoreActivityData: response.data.data.length === this.activityDataBatchCount,
            activityLoading: false,
            activityLoaded: true,
          });
        }

        this.processData();
      })
      .catch((thrown) => {});

    axios
      .get(
        `${window.apiUrl}/behaviour/${selectedPupil.id}${dateQuery}`,
        {
          cancelToken: this.axiosCancelSource.token,
        }
      )
      .then((response) => {
        if (response.data.success) {
          this.setState({
            behaviourMeta: response.data.meta,
            behaviourData: response.data.data,
          });
        }

        this.processData();

        this.setState({
          behaviourLoading: false,
          behaviourLoaded: true,
        });
      })
      .catch((thrown) => {});

    this.setState({
      displayActivity: selectedPupil.display_activity,
    });
  }

  processData() {
    const {
      t,
    } = this.props;
    const {
      behaviourMeta,
      behaviourData,
    } = this.state;

    function sum(obj) {
      if (!obj || Object.keys(obj).length === 0) {
        return 0;
      }
      return Object.keys(obj).reduce(
        (sum, key) => sum + parseFloat(obj[key] || 0),
        0,
      );
    }

    const data = [
      {
        name: t('Positive'),
        y: sum(behaviourData.positive_reasons),
        color: '#8AC44B',
        type: 'positive',
        drilldown: {
          name: t('Positive behaviours'),
          categories: behaviourData.positive_reasons
            ? Object.keys(behaviourData.positive_reasons)
            : [],
          data: behaviourData.positive_reasons
            ? Object.values(behaviourData.positive_reasons)
            : [],
          color: '#8AC44B',
        },
      },
      {
        name: t('Negative'),
        y: sum(behaviourData.negative_reasons),
        color: '#BB2D2D',
        type: 'negative',
        drilldown: {
          name: t('Negative behaviours'),
          categories: behaviourData.negative_reasons
            ? Object.keys(behaviourData.negative_reasons)
            : [],
          data: behaviourData.negative_reasons
            ? Object.values(behaviourData.negative_reasons)
            : [],
          color: '#BB2D2D',
        },
      },
    ];

    let primaryData = [];
    let secondaryData = [];

    for (let i = 0; i < data.length; i++) {
      // add browser data
      primaryData.push({
        name: data[i].name,
        y: data[i].y,
        color: data[i].color,
        type: data[i].type,
      });

      // add version data
      for (let j = 0; j < data[i].drilldown.data.length; j++) {
        const brightness = 0.2 - j / data[i].drilldown.data.length / 5;
        secondaryData.push({
          name: data[i].drilldown.categories[j],
          y: data[i].drilldown.data[j],
          color: Highcharts.Color(data[i].color)
            .brighten(brightness)
            .get(),
          type: data[i].type,
        });
      }
    }

    const behaviourChartConfig = {
      chart: {
        height: 400,
        type: 'pie',
      },
      title: {
        text: t('Behaviour score breakdown'),
      },
      yAxis: {
        title: {
          text: t('Behaviour points'),
        },
      },
      plotOptions: {
        pie: {
          cursor: 'pointer',
          shadow: false,
          center: ['50%', '50%'],
          dataLabels: {
             enabled: !isMobileApp,
         },
        },
      },
      tooltip: {
        valueSuffix: '',
        formatter() {
          let content = `<b>${
            this.point.name
          }</b><br /><small>${
            this.point.type === 'negative' ? '-' : '+'
          }${this.y
          } (${
            Math.round(this.percentage)
          }%)</small>`;

          if (this.point.name === t('Other positive')) {
            content += '<br />';
            for (const name in behaviourData.other_positive_count) {
              content = `${content}<br />${name}: ${behaviourData.other_positive_count[name]}`;
            }
          } else if (this.point.name === t('Other negative')) {
            content += '<br />';
            for (const name in behaviourData.other_negative_count) {
              content = `${content}<br />${name}: ${behaviourData.other_negative_count[name]}`;
            }
          }

          return content;
        },
      },
      series: [
        {
          name: t('Behaviour'),
          data: primaryData,
          size: '40%',
          dataLabels: {
            formatter() {
              return null;
            },
            color: 'white',
            distance: -30,
          },
        },
        {
          name: t('Behaviour type'),
          data: secondaryData,
          innerSize: '40%',
          dataLabels: {
            formatter() {
              let { name } = this.point;
              if (name.length > 15) {
                name = `${name.substr(0, 14)}...`;
              }

              return this.percentage >= 3
                ? `<b>${
                  name
                }</b> ${
                  this.point.type === 'negative' ? '-' : '+'
                }${this.y
                }`
                : null;
            },
          },
        },
      ],
      credits: { enabled: false },
    };

    if (isMobileApp) {
      behaviourChartConfig.subtitle = {
        text: t('Click on a segment to view more information'),
      };
    }

    this.setState({
      behaviourChartConfig,
      timeChartEnabled: behaviourData.timeline && behaviourData.timeline.length > 0,
      dateStart: behaviourMeta.start_date,
      dateEnd: behaviourMeta.end_date,
    });

    // Time chart
    if (this.state.timeChartEnabled) {
      const timelineFilter = () => {
        if (typeof behaviourData.timeline[this.x] !== 'undefined') {
          this.setState({
            dateStart: behaviourData.timeline[this.x].start,
            dateEnd: behaviourData.timeline[this.x].end,
          }, () => {
            this.reloadData();
          });
        }
      };

      // TODO: replace with getColumnChartConfig() from chart-helpers.js
      const timeChartConfig = {
        chart: {
          height: 400,
          type: 'column',
        },
        colors: ['#8AC44B', '#BB2D2D', '#4572A7'],
        title: {
          text:
            behaviourMeta.step_size === 'month'
              ? t('Monthly Behaviour Breakdown')
              : behaviourMeta.step_size === 'week'
                ? t('Weekly Behaviour Breakdown')
                : t('Daily Behaviour Breakdown'),
        },
        credits: { enabled: false },
        plotOptions: {
          column: {
            cursor: 'pointer',
            stacking: 'normal',
          },
        },
        xAxis: {
          categories: behaviourData.timeline.map(el => el.name),
          labels: {
            rotation: -45,
            align: 'right',
            style: {
              fontSize: '13px',
              fontFamily: 'Verdana, sans-serif',
            },
          },
        },
        yAxis: [
          {
            title: {
              text: t('Behaviour points'),
            },
          },
          {
            title: {
              text: t('Ratio'),
            },
            labels: {
              formatter() {
                return `${this.value}%`;
              },
              style: {
                color: '#4572A7',
              },
            },
            min: 0,
            max: 100,
            opposite: true,
          },
        ],
        tooltip: {
          formatter() {
            let total = 0;
            for (let i = 0; i < this.series.length; i++) {
              if (this.series[i].name !== 'Ratio') {
                for (let j = 0; j < this.series[i].points.length; j++) {
                  if (
                    this.series[i].points[j].category === this.point.category
                  ) {
                    total += this.series[i].points[j].y;
                  }
                }
              }
            }

            return (
              `<b>${
                this.point.category
              }</b><br />${
                this.series.name
              }: ${
                this.y
              }${this.series.name === 'Ratio' ? '%' : ''
              }<br />${
                t('Total')
              }: ${
                total}`
            );
          },
        },
        series: [
          {
            name: t('Positive'),
            stack: 'stack',
            data: behaviourData.timeline.map(el => (el.positive ? parseInt(el.positive) : null)),
            point: {
              events: {
                click: timelineFilter,
              },
            },
          },
          {
            name: t('Negative'),
            stack: 'stack',
            data: behaviourData.timeline.map(el => (el.negative ? parseInt(el.negative) : null)),
            point: {
              events: {
                click: timelineFilter,
              },
            },
          },
          {
            name: t('Ratio'),
            type: 'spline',
            yAxis: 1,
            data: behaviourData.timeline.map(el => (el.positive || el.negative
              ? Math.round((el.positive / (el.positive - el.negative)) * 100)
              : 0)),
          },
        ],
      };

      // Check if we have any negatives
      const negative_data_points = timeChartConfig.series[1].data.length;
      let negative_data_points_empty = 0;
      for (let i = 0; i < negative_data_points; i++) {
        if (timeChartConfig.series[1].data[i] === null) {
          negative_data_points_empty++;
        }
      }
      const has_negative = negative_data_points !== negative_data_points_empty;
      if (!has_negative) {
        timeChartConfig.series.splice(2, 1);
        timeChartConfig.series.splice(1, 1);
      }

      this.setState({ timeChartConfig });
    }
  }

  render() {
    const { classes, t } = this.props;
    const {
      activityData,
      activityMeta,
      behaviourData,
      behaviourMeta,
      behaviourChartConfig,
      timeChartEnabled,
      timeChartConfig,
      dateStart,
      dateEnd,
      displayActivity,
      hasMoreActivityData,
      isActivityEntryDeleteDialogOpen,
    } = this.state;

    const hasBehaviourChartData = (behaviourData.length > 0 || Object.keys(behaviourData).length > 0)
      && (behaviourData.negative_reasons.length > 0
        || Object.keys(behaviourData.negative_reasons).length > 0
        || behaviourData.positive_reasons.length > 0
        || Object.keys(behaviourData.positive_reasons).length > 0);

    let lastDate = '';

    return (
      <div className="behaviour-page">
        <CalendarHeader
          dateText={`${t('Date')}:`}
          dateStart={dateStart}
          dateEnd={dateEnd}
          onRangeChanged={this.onRangeChanged}
          useSuspense={false}
        />
        {!hasBehaviourChartData && (
          <div>No activity found for the selected date range.</div>
        )}
        {hasBehaviourChartData && (
          <>
            <HighchartsReact
              highcharts={Highcharts}
              options={behaviourChartConfig}
              ref="behaviourChart"
            />
            {timeChartEnabled && (
              <HighchartsReact
                highcharts={Highcharts}
                options={timeChartConfig}
                ref="timeChart"
              />
            )}
          </>
        )}
        {displayActivity && (
          <div className={classes.activitySection}>
            <Typography
              className={classes.activitySectionHeading}
              variant="h5"
              component="h5"
            >
              {t('Activity')}
            </Typography>
            {activityData.length === 0 && (
              <p>{t('No activity found for the selected date range.')}</p>
            )}
            {activityData.length > 0 && activityData.map((activityEntry) => {
                const date = moment(activityEntry.timestamp).format(
                  'YYYY-MM-DD',
                );

                if (lastDate !== date) {
                  lastDate = date;

                  return (
                    <div key={activityEntry.id}>
                      <Typography
                        className={classes.activitySectionSubHeading}
                        variant="h6"
                        component="h6"
                      >
                        {moment(date).format('dddd D MMMM')}
                      </Typography>
                      <ActivityEntry
                        key={activityEntry.id}
                        data={activityEntry}
                        dateFormat={activityMeta.date_format}
                        detentionAliasUc={activityMeta.detention_alias_uc}
                        onDelete={this.onActivityEntryDelete}
                        useSuspense={false}
                      />
                    </div>
                  );
                }

                return (
                  <ActivityEntry
                    key={activityEntry.id}
                    data={activityEntry}
                    dateFormat={activityMeta.date_format}
                    detentionAliasUc={activityMeta.detention_alias_uc}
                    onDelete={this.onActivityEntryDelete}
                    useSuspense={false}
                  />
                );
              })}
              <BottomScrollListener onBottom={this.onBottom} offset={100}>
                {hasMoreActivityData &&
                  <div className={classes.activityProgress}>
                    <CircularProgress />
                  </div>
                }
              </BottomScrollListener>
          </div>
        )}
        <ConfirmDialog
          useSuspense={false}
          title={t('Confirmation')}
          isOpen={isActivityEntryDeleteDialogOpen}
          onClose={() => this.setState({
            isActivityEntryDeleteDialogOpen: false,
          })}
          onConfirm={this.onActvityEntryDeleteConfirmClick}
        >
          {t('Are you sure you want to delete this behaviour?')}
        </ConfirmDialog>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(withTranslation()(withSnackbar(BehaviourPage)))
);
